import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/src/atoms/button';
import { Modal } from '@savgroup-front-common/core/src/atoms/modal';
import { toast } from '@savgroup-front-common/core/src/atoms/Toast';
import { PageHeader } from '@savgroup-front-common/core/src/molecules/PageHeader';
import { RocketLaunchIcon } from '@savgroup-front-common/core/src/protons/icons';

import SubmitRuleModal from './SubmitRuleModal';
import SubmitRuleSummarySection from './SubmitRuleSummarySection';
import { SUBMIT_RULE_CATEGORIES } from './submitRuleMockData';
import type { SubmitRuleCategory } from './submitRuleMockData';
import {
  countPendingBlocks,
  countPendingRules,
} from './submitRulePage.helpers';
import type { PublishScope } from './submitRulePage.types';
import {
  $EmptyPublishedState,
  $FooterActions,
  $PageContainer,
  $PageSubtitle,
} from './SubmitRulePage.styles';

const RULES_LIST_ROUTE = '/administration/console-regles';

const cloneInitialCategories = (): SubmitRuleCategory[] =>
  SUBMIT_RULE_CATEGORIES.map((category) => ({
    ...category,
    rules: category.rules.map((rule) => ({
      ...rule,
      cells: rule.cells.map((cell) => ({ ...cell })),
    })),
  }));

const SubmitRulePage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<SubmitRuleCategory[]>(cloneInitialCategories);
  const [publishedImportTypes, setPublishedImportTypes] = useState<string[]>([]);
  const [publishScope, setPublishScope] = useState<PublishScope | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pendingCategories = useMemo(
    () => categories.filter((category) => category.rules.length > 0),
    [categories],
  );

  const pendingRuleCount = useMemo(() => countPendingRules(categories), [categories]);
  const pendingBlockCount = useMemo(() => countPendingBlocks(categories), [categories]);
  const hasRulesToSubmit = pendingRuleCount > 0;
  const isAllPublished = pendingRuleCount === 0 && publishedImportTypes.length > 0;

  const handleOpenPublishBlock = useCallback(
    (importType: string) => {
      const category = categories.find((item) => item.importType === importType);

      if (!category || category.rules.length === 0) {
        return;
      }

      setPublishScope({
        type: 'block',
        importType: category.importType,
        title: category.title,
        ruleCount: category.rules.length,
      });
    },
    [categories],
  );

  const handleOpenPublishAll = useCallback(() => {
    if (pendingRuleCount === 0) {
      return;
    }

    setPublishScope({
      type: 'all',
      ruleCount: pendingRuleCount,
      blockCount: pendingBlockCount,
      blockTitles: pendingCategories.map((category) => category.title),
    });
  }, [pendingBlockCount, pendingCategories, pendingRuleCount]);

  const handleCloseSubmitModal = useCallback(() => {
    setPublishScope(undefined);
  }, []);

  const handleReturnToRulesList = useCallback(() => {
    navigate(RULES_LIST_ROUTE);
  }, [navigate]);

  const publishCategories = useCallback(
    (importTypesToPublish: string[], comment: string) => {
      const publishedTitles: string[] = [];
      let publishedRuleCount = 0;

      setCategories((previous) =>
        previous.map((category) => {
          if (!importTypesToPublish.includes(category.importType)) {
            return category;
          }

          publishedTitles.push(category.title);
          publishedRuleCount += category.rules.length;

          return { ...category, rules: [] };
        }),
      );

      setPublishedImportTypes((previous) => [
        ...new Set([...previous, ...importTypesToPublish]),
      ]);

      if (publishedTitles.length === 1) {
        toast.success(
          `Bloc « ${publishedTitles[0]} » publié (${publishedRuleCount} règle(s)). Commentaire : « ${comment} »`,
        );
      } else {
        toast.success(
          `${publishedRuleCount} règle(s) publiées dans ${publishedTitles.length} bloc(s). Commentaire : « ${comment} »`,
        );
      }
    },
    [],
  );

  const handleSubmitPublish = useCallback(
    async (comment: string) => {
      if (!publishScope) {
        return;
      }

      setIsSubmitting(true);

      await new Promise((resolve) => {
        setTimeout(resolve, 800);
      });

      if (publishScope.type === 'block') {
        publishCategories([publishScope.importType], comment);
      } else {
        publishCategories(
          pendingCategories.map((category) => category.importType),
          comment,
        );
      }

      setIsSubmitting(false);
      setPublishScope(undefined);
    },
    [pendingCategories, publishCategories, publishScope],
  );

  return (
    <$PageContainer>
      <PageHeader level={2}>Récapitulatif des modifications</PageHeader>
      {hasRulesToSubmit && (
        <$PageSubtitle>
          {pendingRuleCount} règle(s) en attente dans {pendingBlockCount} bloc(s). Vous pouvez
          publier bloc par bloc ou tout publier d&apos;un coup.
        </$PageSubtitle>
      )}
      {isAllPublished && (
        <$PageSubtitle>Toutes les modifications en attente ont été publiées.</$PageSubtitle>
      )}

      {publishScope && (
        <Modal isOpen onClose={handleCloseSubmitModal}>
          <SubmitRuleModal
            scope={publishScope}
            onSubmit={handleSubmitPublish}
            isLoading={isSubmitting}
          />
        </Modal>
      )}

      <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {categories.map((category) => (
          <SubmitRuleSummarySection
            key={category.importType}
            category={category}
            isPublished={publishedImportTypes.includes(category.importType)}
            onPublishBlock={handleOpenPublishBlock}
          />
        ))}
      </div>

      {isAllPublished && (
        <$EmptyPublishedState>
          <p style={{ margin: '0 0 1rem' }}>Aucune règle en attente de publication.</p>
          <Button primary type={BUTTON_TYPES.BUTTON} onClick={handleReturnToRulesList} small>
            Retour à la console
          </Button>
        </$EmptyPublishedState>
      )}

      <$FooterActions>
        <Button tertiary type={BUTTON_TYPES.BUTTON} onClick={handleReturnToRulesList} small>
          Retour à la console
        </Button>
        <Button
          primary
          type={BUTTON_TYPES.BUTTON}
          onClick={handleOpenPublishAll}
          icon={<RocketLaunchIcon />}
          disabled={!hasRulesToSubmit}
          small
        >
          {hasRulesToSubmit ? `Tout publier (${pendingRuleCount})` : 'Tout publier'}
        </Button>
      </$FooterActions>
    </$PageContainer>
  );
};

export default SubmitRulePage;
